<script>
import { mapGetters, mapState } from 'vuex'
import WorkflowWidget from '@/components/common/WorkflowWidget.vue'
import { saveAs } from 'file-saver'
import axios from 'axios'
import moment from 'moment/moment'

export default {
  name: 'ExecutionProcess',
  components: { WorkflowWidget },
  props: {
    execution: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      can: 'can'
    })
  },
  data() {
    return {
      xmlData: null,
      height: 400,
      showOverlay: true,
      selectedActivity: null,
      lastElementId: 'Activity_1p6sgoe',
      fullSize: false,
      render: false,
      diagramLoader: false,
      activityMetrics: {},
      nodesToBeMarked: [],
      nodesToBeTagged: [],
      leafNodes: [],
      failedNodes: []
    }
  },
  mounted() {
    this.init()
    this.getData()
  },
  methods: {
    init() {
      this.getWorkflowDiagram()
      this.parseBPMN(this.xmlData)
    },
    getWorkflowDiagram() {
      //Try to get the diagram from the process instance
      axios
        .get('/api/core/process-diagram-by-execution/' + this.execution.hexId)
        .then(res => {
          this.xmlData = res.data.bpmn20Xml
        })
        .catch(e => {
          console.log(e)
          //If the process instance does not have a diagram, try to get it from the workflow
          axios
            .get('/api/v1/workflows/' + this.execution.workflow)
            .then(res => {
              this.xmlData = res.data.data
            })
            .catch(e => {
              console.log(e)
            })
        })
    },
    async getData() {
      let executionCircleHTML =
        '<div style="background: green;display: flex;align-items: center;justify-content: center;width: 40px;height: 40px; border-radius: 50%">'

      let executionFailedHTML =
        '<div style="background: red; padding: 5px; display: flex;align-items: center;justify-content: center;height: 40px;border-radius: 10px">Failed</div>'

      let executionRunningHTML =
        '<div style="background: blue; padding: 5px; display: flex;align-items: center;justify-content: center;height: 40px;border-radius: 10px">Running</div>'

      let executionWaitingHTML =
        '<div style="background: orange; padding: 5px; display: flex;align-items: center;justify-content: center;height: 40px;border-radius: 10px">Waiting</div>'

      this.render = false
      await this.getActivityMetrics()
      this.nodesToBeMarked = []
      this.activityMetrics.forEach(metric => {
        let node = {}
        node.id = metric.id
        if (metric.type === 'executed') {
          node.position = {
            bottom: 15,
            right: 15
          }
          node.html = executionCircleHTML + metric.count + '</div>'
        } else if (metric.type === 'failed') {
          node.position = {
            bottom: 15,
            left: 5
          }
          node.html = executionFailedHTML
        } else if (metric.type === 'running') {
          node.position = {
            bottom: 15,
            left: 5
          }
          node.html = executionRunningHTML
        } else if (metric.type === 'active') {
          node.position = {
            bottom: 15,
            left: 5
          }
          node.html = executionWaitingHTML
        }

        this.nodesToBeMarked.push(node)
      })

      this.render = true
    },
    async getActivityMetrics() {
      this.diagramLoader = true
      await axios
        .get(`/api/v1/states/${this.execution.state}/statistics`)
        .then(res => {
          this.activityMetrics = res.data
          this.diagramLoader = false
        })
        .catch(e => {
          // notify user
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Error fetching activity metrics',
            description:
              'Error fetching activity metrics: ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          console.log(e)
          this.diagramLoader = false
        })
    },
    handleSelection(event) {
      if (event.element.id && event.element.type !== 'bpmn:Process') {
        this.selectedActivity = event.element.id
      } else {
        this.selectedActivity = null
      }
      this.$emit('activitySelected', this.selectedActivity)
    },
    toggleResize() {
      this.fullSize = !this.fullSize
    },
    refresh() {
      this.diagramLoader = true
      this.diagramLoader = false
    },
    saveXML() {
      this.$refs.workflowWidget
        .getBpmnCanvas()
        .saveXML({ format: true }, (err, xml) => {
          if (!err) {
            saveAs(
              new Blob([xml], { type: 'text/xml;charset=utf-8' }),
              'workflow.bpmn'
            )
          }
        })
    },
    saveSVG() {
      this.$refs.workflowWidget.getBpmnCanvas().saveSVG((err, svg) => {
        if (!err) {
          saveAs(
            new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
            'workflow.svg'
          )
        }
      })
    },
    parseBPMN(xml) {
      let elementHTML =
        '<div style="background: gray; padding: 5px; display: flex;align-items: center;justify-content: center;height: 30px; max-width: 100px; white-space: nowrap; border-radius: 10px">'
      this.nodesToBeTagged = []
      //parse bpmn xml
      let parser = new DOMParser()
      let xmlDoc = parser.parseFromString(xml, 'text/xml')
      //get all the elements with tag name bpmn:serviceTask
      let serviceTasks = xmlDoc.getElementsByTagName('bpmn:serviceTask')
      //get all the elements with tag name bpmn:userTask
      let userTasks = xmlDoc.getElementsByTagName('bpmn:userTask')
      //iterate over all the service tasks
      Array.from(serviceTasks).forEach(element => {
        let node = {}
        node.id = element.getAttribute('id')
        node.position = {
          top: -15,
          right: 40
        }
        let name = ''
        let customClass = element.getAttribute('camunda:class')
        switch (customClass) {
          case 'com.beezlabs.camundaengine.async.Appender':
            name = 'Appender'
            break
          case 'com.beezlabs.camundaengine.async.Bot':
            let customAgentType = element.getAttribute('tulip:hiveType')
            if (customAgentType === 'JAVA') {
              name = 'Java Bot'
            } else if (customAgentType === 'RPA') {
              name = 'RPA Bot'
            } else if (customAgentType === 'SAP') {
              name = 'SAP Bot'
            }
            break
          case 'com.beezlabs.camundaengine.async.BotTrigger':
            name = 'Bot Trigger'
            break
          case 'com.beezlabs.camundaengine.async.Iterator':
            name = 'Iterator'
            break
          case 'com.beezlabs.camundaengine.async.Refactor':
            name = 'Refactor'
            break
          case 'com.beezlabs.camundaengine.async.UnMap':
            name = 'UnMap'
            break
          case 'com.beezlabs.camundaengine.async.WorkflowTrigger':
            name = 'Child Workflow trigger'
            break
          default:
            name = null
        }
        node.html = elementHTML + name + '</div>'
        this.nodesToBeTagged.push(node)
      })
      Array.from(userTasks).forEach(element => {
        let node = {}
        node.id = element.getAttribute('id')
        node.position = {
          top: -15,
          right: 40
        }
        node.html = elementHTML + 'User Task</div>'
        this.nodesToBeTagged.push(node)
      })
    }
  }
}
</script>

<template>
  <v-card flat>
    <v-progress-linear
      class="ma-0"
      height="5"
      indeterminate
      :active="diagramLoader"
      color="accent"
    />
    <v-layout xs12 align-start justify-start row>
      <v-tooltip bottom>
        <v-btn class="pa-1" icon @click.stop="toggleResize" slot="activator">
          <v-icon>{{ fullSize ? 'fullscreen_exit' : 'zoom_out_map' }}</v-icon>
        </v-btn>
        <span>{{ fullSize ? 'Collapse' : 'Expand' }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          :loading="diagramLoader"
          class="pa-1"
          icon
          @click.stop="getData"
          slot="activator"
        >
          <v-icon>cached</v-icon>
        </v-btn>
        <span>Refresh</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          :loading="diagramLoader"
          class="pa-1"
          icon
          @click.stop="showOverlay = !showOverlay"
          slot="activator"
        >
          <v-icon>{{ showOverlay ? 'layers_clear' : 'layers' }}</v-icon>
        </v-btn>
        <span>Show/Hide Overlay</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          :loading="diagramLoader"
          class="pa-1"
          icon
          @click.stop="saveXML"
          slot="activator"
        >
          <v-icon>get_app</v-icon>
        </v-btn>
        <span>Download Diagram as XML'</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          :loading="diagramLoader"
          class="pa-1"
          icon
          @click.stop="saveSVG"
          slot="activator"
        >
          <v-icon>image</v-icon>
        </v-btn>
        <span>Download Diagram as SVG</span>
      </v-tooltip>
    </v-layout>
    <v-card-text
      v-if="xmlData && render"
      :class="
        fullSize
          ? 'workflow-widget-full-size '
          : 'workflow-widget ' +
            'fill-height workflow-widget__background pa-4 mb-2'
      "
    >
      <workflow-widget
        :bpmn-xml="xmlData"
        zoom-reset-button
        :editable="false"
        zoom-in-button
        zoom-out-button
        :nodes-to-be-marked="showOverlay ? nodesToBeMarked : []"
        :tag-workflow-items="showOverlay"
        @element_click="handleSelection"
        ref="workflowWidget"
      >
      </workflow-widget>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.workflow-widget {
  height: 400px;
}
.workflow-widget-full-size {
  height: 800px;
}

.workflow-widget__background {
  background: #fafafa;
}
.highlight:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: green !important; /* color elements as green */
}
</style>
